import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  RefreshCw,
  Printer,
  ArrowUpDown,
  Share2,
  LogOut,
  FileDown,
  CalendarRange,
  Eye,
  Trash2,
  MoreVertical
} from 'lucide-react';

export const DashboardActions = ({
  showRefreshButton,
  showPrintButton,
  showClearScheduleButton,
  showOrderHotelsButton,
  showExportInvoiceButton,
  showShareButton,
  // NOVO: botão para copiar a semana atual para a próxima
  showCopyScheduleButton,
  // NOVO: libera/esconde a semana de schedule para as funcionárias
  showScheduleVisibilityButton,
  onRefresh,
  onPrint,
  onClearSchedule,
  onOrderHotels,
  onExportInvoice,
  onShare,
  // NOVO: handler do botão de copiar semana
  onCopySchedule,
  onToggleScheduleVisibility,
  onLogout,
  isGeneratingImage,
  dashboardData,
  loadingDashboard,
   isEmployeeClockedIn 
}) => {
  const theme = useTheme();
  // Em telas estreitas os botões não cabem na toolbar — viram um menu de "...".
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [menuAnchor, setMenuAnchor] = useState(null);

  const shareDisabled =
    !dashboardData || isGeneratingImage || loadingDashboard || isEmployeeClockedIn;

  // Uma lista só, usada tanto pelos botões do desktop quanto pelo menu do mobile.
  const actions = [
    {
      key: 'refresh',
      show: showRefreshButton,
      label: 'Refresh',
      onClick: onRefresh,
      icon: <RefreshCw size={18} />,
      bg: 'linear-gradient(to right, #e0e0e0, #bdbdbd)',
      bgHover: 'linear-gradient(to right, #9e9e9e, #757575)',
      color: '#444'
    },
    {
      key: 'order',
      show: showOrderHotelsButton,
      label: 'Order resorts',
      onClick: onOrderHotels,
      icon: <ArrowUpDown size={18} />,
      bg: 'linear-gradient(to right,rgb(106, 90, 205),rgb(129, 119, 240))',
      bgHover: 'linear-gradient(to right,rgb(85, 68, 185),rgb(108, 98, 220))'
    },
    {
      key: 'print',
      show: showPrintButton,
      label: 'Print',
      onClick: onPrint,
      icon: <Printer size={18} />,
      bg: 'linear-gradient(to right,rgb(57, 68, 230),rgb(21, 139, 207))',
      bgHover: 'linear-gradient(to right,rgb(33, 31, 162),rgb(35, 84, 139))'
    },
    {
      key: 'clear',
      show: showClearScheduleButton,
      label: 'Delete schedule',
      onClick: onClearSchedule,
      icon: <Trash2 size={18} />,
      bg: 'linear-gradient(to right,rgb(255, 145, 0),rgb(252, 201, 61))',
      bgHover: 'linear-gradient(to right,rgb(192, 108, 39),rgb(206, 143, 26))'
    },
    {
      key: 'copy',
      show: showCopyScheduleButton,
      label: 'Copy schedule',
      onClick: onCopySchedule,
      icon: <CalendarRange size={18} />,
      bg: 'linear-gradient(to right, #8e24aa, #ab47bc)',
      bgHover: 'linear-gradient(to right, #6a1b9a, #8e24aa)'
    },
    {
      key: 'visibility',
      show: showScheduleVisibilityButton,
      label: 'Employee visibility',
      onClick: onToggleScheduleVisibility,
      icon: <Eye size={18} />,
      bg: 'linear-gradient(to right, #00897b, #26a69a)',
      bgHover: 'linear-gradient(to right, #00695c, #00897b)'
    },
    {
      key: 'invoice',
      show: showExportInvoiceButton,
      label: 'Export invoice',
      onClick: onExportInvoice,
      icon: <FileDown size={18} />,
      bg: 'linear-gradient(to right, #1e88e5, #0d47a1)',
      bgHover: 'linear-gradient(to right, #1565c0, #003c8f)'
    },
    {
      key: 'share',
      show: showShareButton,
      label: 'Share daily',
      onClick: onShare,
      disabled: shareDisabled,
      icon: isGeneratingImage
        ? <CircularProgress size={18} color="inherit" />
        : <Share2 size={18} />,
      bg: 'linear-gradient(to right, #4caf50, #66bb6a)',
      bgHover: 'linear-gradient(to right, #388e3c, #4caf50)'
    }
  ].filter((a) => a.show);

  const logoutButton = (
    <Button
      onClick={onLogout}
      sx={{
        background: 'linear-gradient(to right, #ff5252, #ff1744)',
        color: '#fff',
        fontWeight: 'bold',
        px: 2,
        minWidth: 'auto',
        '&:hover': {
          background: 'linear-gradient(to right, #d50000, #c51162)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
        }
      }}
    >
      <LogOut size={18} />
    </Button>
  );

  if (isNarrow && actions.length > 0) {
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <IconButton
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{ color: 'rgb(127, 127, 127)' }}
        >
          <MoreVertical size={20} />
        </IconButton>

        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {actions.map((a) => (
            <MenuItem
              key={a.key}
              disabled={a.disabled}
              onClick={() => { setMenuAnchor(null); a.onClick?.(); }}
            >
              <ListItemIcon sx={{ minWidth: 34 }}>{a.icon}</ListItemIcon>
              <ListItemText>{a.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>

        {logoutButton}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {actions.map((a) => (
        <Button
          key={a.key}
          onClick={a.onClick}
          disabled={a.disabled}
          sx={{
            background: a.bg,
            color: a.color || '#fff',
            fontWeight: 'bold',
            px: 2,
            minWidth: 'auto',
            opacity: a.disabled ? 0.5 : 1,
            '&:hover': {
              background: a.bgHover,
              color: '#fff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }
          }}
        >
          {a.icon}
        </Button>
      ))}

      {logoutButton}
    </Box>
  );
};
